import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Camera, Sparkles, FileText, ArrowUpRight, CheckCircle2 } from "lucide-react";

/**
 * AI Property Audit — Home-page explainer.
 * Plain-English pitch of what the audit does + a sample "output" preview
 * so users understand the value before clicking through.
 */
const STEPS = [
  {
    Icon: Camera,
    n: "01",
    title: "Snap one photo",
    body: "Storefront, facade, sidewalk, entrance — phone camera works. No angles, no ladders.",
  },
  {
    Icon: Sparkles,
    n: "02",
    title: "Our AI reads it",
    body: "Gemini vision matches what it sees against eleven years of Bespoke field records — biological growth, efflorescence, gum, staining, oxidation.",
  },
  {
    Icon: FileText,
    n: "03",
    title: "Get your written report",
    body: "Visible issues, prioritized services, suggested service cadence, and a rough tier — sent to you in under a minute. Free, no signup.",
  },
];

const SAMPLE_ISSUES = [
  "Algae band on north facade",
  "Efflorescence on limestone sills",
  "Heavy gum on sidewalk apron",
  "UV fade on awning fabric",
];

export default function AuditIntro() {
  return (
    <section
      className="py-28 md:py-36 relative overflow-hidden"
      style={{ background: "var(--bg)" }}
      data-testid="audit-intro"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at 15% 30%, rgba(198,168,124,0.06), transparent 55%)"
      }} />
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-3 overline pt-2">The AI Property Audit</div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ letterSpacing: "-0.035em" }}>
              Not sure what your property needs?{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Ask the AI first.</span>
            </h2>
            <p className="mt-8 max-w-2xl font-body text-lg md:text-xl" style={{ color: "var(--text-2)", lineHeight: 1.7 }}>
              Upload a single photo of your storefront or building. Our AI reads it, flags every visible exterior issue, and hands you a written maintenance report — in under a minute. <em>Free. No signup. No calls.</em>
            </p>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="glass-card p-10"
              data-testid={`audit-step-${s.n}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-14 h-14 flex items-center justify-center" style={{
                  borderRadius: "var(--r-full)",
                  border: "1px solid var(--line-gold)",
                  background: "rgba(198,168,124,0.06)"
                }}>
                  <s.Icon size={18} strokeWidth={1.4} style={{ color: "var(--gold)" }} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "var(--text-3)" }}>{s.n}</div>
              </div>
              <h3 className="mt-8 font-display text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em", lineHeight: 1.15 }}>{s.title}</h3>
              <p className="mt-5 font-body" style={{ color: "var(--text-2)", lineHeight: 1.7, fontSize: 15 }}>{s.body}</p>
            </motion.div>
          ))}
        </div>

        {/* Sample brief teaser */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 grid md:grid-cols-12 gap-6 items-center glass-card p-6 md:p-10"
          data-testid="audit-sample-preview"
        >
          <div className="md:col-span-5">
            <div className="overline mb-3">Example report</div>
            <div className="font-display text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              Two-story retail storefront — Charlotte NC
            </div>
            <p className="mt-4 font-body text-sm" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
              This is a real sample of what our AI returned for a portfolio client. Yours will look just like this — tailored to your photo.
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="overline mb-3">What the AI flagged</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SAMPLE_ISSUES.map((issue, i) => (
                <div key={i} className="flex items-start gap-3 p-3" style={{
                  background: "rgba(198,168,124,0.05)",
                  border: "1px solid rgba(198,168,124,0.18)",
                  borderRadius: "12px",
                }}>
                  <CheckCircle2 size={14} style={{ color: "var(--gold)", marginTop: 2 }} className="shrink-0" />
                  <div className="font-body text-sm" style={{ color: "var(--text)" }}>{issue}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em]" style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "999px",
                color: "var(--text-2)",
              }}>Suggested cadence: Monthly</div>
              <div className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em]" style={{
                background: "rgba(198,168,124,0.12)",
                border: "1px solid rgba(198,168,124,0.3)",
                borderRadius: "999px",
                color: "var(--gold-2)",
              }}>Guardian tier · $1,499/mo</div>
            </div>
          </div>
        </motion.div>

        <div className="mt-14 flex flex-wrap justify-center items-center gap-5">
          <Link to="/audit" className="btn-glow" data-testid="audit-intro-cta" data-cursor="hover">
            Run My Free Audit <ArrowUpRight size={13} />
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
            Free · Sixty seconds · No signup
          </div>
        </div>
      </div>
    </section>
  );
}
